import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Heart,
  Users,
  Star,
  ArrowRight,
  Shield,
  Clock,
  Phone
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const WhyChoosePage = () => {
  const ourValues = [
    {
      title: "Same Team, Every Visit",
      description: "You'll have the same cleaners every time. They learn your home, your preferences, and become familiar faces you trust. No rotating crews or strangers showing up unannounced."
    },
    {
      title: "Direct Communication",
      description: "When you call, you reach Bob or Heather—not a call center or scheduling system. We're responsive, easy to reach, and personally invested in making sure you're taken care of."
    },
    {
      title: "Long-Term Relationships",
      description: "We're not chasing one-time jobs. We build relationships with clients who stay with us for years. Many of our recurring customers have been with us since we started, and that's what we value most."
    },
    {
      title: "Accountability",
      description: "We're locally owned and stand behind our work. If something isn't right, you call us directly and we fix it. No bureaucracy, no run-around—just straightforward accountability."
    }
  ];

  const whatToExpect = [
    "Same cleaning team on every visit",
    "Direct communication with Bob and Heather",
    "Respectful of your home and belongings",
    "On-time, reliable service",
    "Responsive to your questions and requests",
    "Professional, experienced cleaning",
    "Insured and bonded for your protection",
    "Fair pricing, no hidden fees"
  ];

  const trustFAQs = [
    {
      question: "Why should I choose a local company over a franchise?",
      answer: "With a local business like Pierce Premier, you work directly with the owners—Bob and Heather. We're accountable to you, not a corporate office. You'll have the same team every visit, direct communication, and the flexibility that comes with working with people who genuinely care about your satisfaction. We've built our business on long-term relationships, not volume."
    },
    {
      question: "What makes Heather qualified to run a cleaning business?",
      answer: "Heather has over 15 years of executive housekeeping experience, working with high-end properties and managing cleaning operations professionally. She brings that level of expertise, attention to detail, and quality standards to every Pierce Premier job. This isn't a side hustle—it's her career and specialty."
    },
    {
      question: "How do I know I can trust you in my home?",
      answer: "We're fully insured and bonded. Our team members are carefully selected, and because you have the same cleaners every visit, they become familiar, trusted people in your home. We're also a local business with a reputation in Brevard County to protect—your trust matters to us personally and professionally."
    },
    {
      question: "What if I'm not satisfied with the cleaning?",
      answer: "Call us. We'll come back and make it right. Because we're a small, locally-owned business, we take every concern seriously. Your satisfaction is tied directly to our reputation, and we stand behind our work."
    },
    {
      question: "Do you have employees or is it just Bob and Heather?",
      answer: "We have a small, consistent team that we personally oversee. Bob and Heather manage the business, coordinate schedules, and ensure quality on every job. You'll have the same cleaners assigned to your home who work under our supervision."
    },
    {
      question: "Why do your customers stay with you long-term?",
      answer: "Consistency and trust. When you have the same team, direct communication, and reliable service every time, cleaning becomes one less thing to worry about. Many of our clients have been with us for years because we've earned their trust and made their lives easier—that's what we're here for."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Why Choose Pierce Premier | Trusted House Cleaning in Brevard County",
      description: "Meet Bob and Heather, learn why Brevard County residents choose Pierce Premier, and discover what makes a locally-owned, relationship-focused cleaning company different.",
      url: "/why-choose-us"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Why Choose Pierce Premier" }
    ]),
    generateLocalBusinessSchema(),
    generateFAQSchema(trustFAQs)
  );

  return (
    <>
      <SEO 
        title="Why Choose Pierce Premier | Trusted House Cleaning in Brevard County"
        description="Meet Bob and Heather, learn why Brevard County residents choose Pierce Premier, and discover what makes a locally-owned, relationship-focused cleaning company different."
        canonical="/why-choose-us"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Heart className="w-5 h-5 text-blue-600" />
                  <Badge className="bg-blue-600 text-white">Locally Owned</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Why Brevard County Residents Choose Pierce Premier
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  We're Bob and Heather—a husband-and-wife team who started Pierce Premier to provide the kind of cleaning service we'd want for our own home: consistent, trustworthy, and built on real relationships.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  We believe cleaning is personal. It's your home, your space, your comfort. That's why we do things differently—same team every visit, direct communication with us, and a commitment to long-term relationships over one-time transactions.
                </p>
                <TrustSignals variant="detailed" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80" 
                  alt="Pierce Premier owners Bob and Heather - trusted house cleaning in Brevard County"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Who We Are
              </h2>
            </div>
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Pierce Premier was started by Bob and Heather, a husband-and-wife team who wanted to create a cleaning company built on trust, consistency, and genuine relationships with customers.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    <strong>Heather brings over 15 years of executive housekeeping experience</strong> to Pierce Premier. She's worked with high-end properties, managed cleaning operations professionally, and understands what quality looks like at every level. This isn't a side business for us—it's what we do, and we take it seriously.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    <strong>Bob handles operations, scheduling, and customer relationships.</strong> When you call Pierce Premier, you're talking to us—not a call center, not a franchise coordinator. We're personally involved in every job and accountable for every customer.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    We're based in Brevard County, serve the local community, and have built our business one satisfied customer at a time. Many of our clients have been with us for years, and that's exactly the kind of business we want to run—one built on trust that lasts.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                What Makes Us Different
              </h2>
              <p className="text-gray-600">The principles that guide how we run Pierce Premier</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {ourValues.map((value, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Star className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Started Pierce Premier
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    We started Pierce Premier because we saw a gap in the market: most cleaning companies focus on volume and efficiency, but customers want something different—they want trust, consistency, and a team that actually knows their home.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    We built this business around the idea that cleaning is personal. It's not just about getting through a checklist—it's about building a relationship where you trust the people in your home, and they understand what matters to you.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    That's why we prioritize long-term clients over one-time jobs, why you have the same team every visit, and why we're easy to reach. We're not trying to be the biggest cleaning company in Brevard County—we're trying to be the one you trust most.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What You Can Expect When You Work With Us
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whatToExpect.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-blue-100">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Questions People Ask About Pierce Premier
            </h2>
            <div className="space-y-6">
              {trustFAQs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Experience the Difference?
              </h2>
              <p className="text-gray-600 mb-8">
                Join the Brevard County families and businesses who trust Pierce Premier for their cleaning needs.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-blue-200">
                <CardContent className="p-8 text-center">
                  <Phone className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Call Us Directly</h3>
                  <p className="text-gray-600 mb-4">Speak with Bob or Heather</p>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    321-408-1347
                  </a>
                </CardContent>
              </Card>
              <Card className="border-2 border-blue-200">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Request a Quote</h3>
                  <p className="text-gray-600 mb-4">We'll get back to you within 24 hours</p>
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Explore Our Services
              </h2>
              <p className="text-gray-600 mb-8">See how Pierce Premier can help with your specific cleaning needs</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "House Cleaning", link: "/services/house-cleaning" },
                { name: "Deep Cleaning", link: "/services/deep-cleaning" },
                { name: "Move-In/Move-Out", link: "/services/move-in-move-out-cleaning" },
                { name: "Commercial Cleaning", link: "/services/commercial" },
                { name: "Vacation Rentals", link: "/services/vacation-rental-cleaning" },
                { name: "Service Areas", link: "/areas" }
              ].map((service, idx) => (
                <Link 
                  key={idx} 
                  to={service.link} 
                  className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 hover:border-blue-400 transition-colors text-center group"
                >
                  <span className="text-blue-700 font-semibold group-hover:text-blue-900">{service.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-blue-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WhyChoosePage;
